import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { preventDefault, stopPropagation } from '../../../../utils/events'
import { getMegabytesFromBytes } from '../../../../utils/file'
import { FileErrorTooLargeModal, useFileDropzone } from '../../../File'

import {
  ACCEPTED_IMAGE_TYPES,
  MAX_INLINE_SIZE,
} from './useEditorAddFile.constants'
import {
  getInlineImageBase64Attributes,
  getInlineImageDropFiles,
  isInlineImageInsertable,
  toInlineImages,
} from './useEditorAddFile.utils'

export function useEditorAddFile({
  editorRef,
  enableInlineImageAttach = false,
  getInlineImageAttributes = getInlineImageBase64Attributes,
  inlineImageMaxSize = MAX_INLINE_SIZE,
  inlineImageMultiple = true,
  onChange,
  onDragOver,
  onDrop,
}) {
  const [bookmark, setBookmark] = useState(null)
  const [modal, setModal] = useState(null)

  const { t } = useTranslation()

  function handleCloseModal() {
    setModal(null)
  }

  async function insertInlineImageFile(file) {
    const attributes = await getInlineImageAttributes(file)
    const editor = editorRef.current

    if (isInlineImageInsertable({ attributes, editor })) {
      if (bookmark) {
        editor.selection.moveToBookmark(bookmark)
      }

      editor.undoManager.transact(() => {
        const img = editor.dom.create('img', { ...attributes, alt: file.name })

        editor.selection.setNode(img)
      })
    }
  }

  async function insertInlineImageFiles(files) {
    await Promise.all(files.map(insertInlineImageFile))
  }

  function handleFinishUpload(files) {
    insertInlineImageFiles(files)
  }

  const {
    getInputProps,
    getRootProps,
    open: handleOpenFileAttach,
  } = useFileDropzone({
    options: {
      accept: ACCEPTED_IMAGE_TYPES,
      maxSize: inlineImageMaxSize,
      multiple: inlineImageMultiple,
      noClick: true,
    },
    useBase64: true,
    onCloseModal: handleCloseModal,
    onFinishUpload: handleFinishUpload,
    onSetModal: setModal,
  })

  function handleFocusOut() {
    const editor = editorRef.current

    if (editor?.selection) {
      const currentBookmark = editor.selection.getBookmark()

      setBookmark(currentBookmark)
    }
  }

  function handleInsertImageFromWysiwyg() {
    if (editorRef.current?.selection) {
      setBookmark(editorRef.current.selection.getBookmark())
    }

    handleOpenFileAttach()
  }

  function handleChangeAfterResize(editor) {
    onChange(editor.getContent())
  }

  async function insertInlineImages(files) {
    if (files.length > 0) {
      handleFinishUpload(await toInlineImages(files))
    }
  }

  function handleConfirmTooLargeModal(files) {
    return event => {
      stopPropagation(event)
      handleCloseModal()

      insertInlineImages(files)
    }
  }

  function handleInlineFilesFromAnySource(fileList) {
    const { acceptedFiles, tooLargeFileNames } = getInlineImageDropFiles({
      files: fileList,
      maxSize: inlineImageMaxSize,
      multiple: inlineImageMultiple,
    })

    if (tooLargeFileNames.length > 0) {
      setModal(
        <FileErrorTooLargeModal
          files={tooLargeFileNames}
          sizeLimit={getMegabytesFromBytes(inlineImageMaxSize)}
          onConfirm={handleConfirmTooLargeModal(acceptedFiles)}
        />
      )
    } else {
      insertInlineImages(acceptedFiles)
    }
  }

  function setup(editor) {
    if (enableInlineImageAttach) {
      editor.ui.registry.addButton('customImageUploading', {
        icon: 'image',
        tooltip: t('File.InsertImageToEditorToolKey'),
        onAction: handleInsertImageFromWysiwyg,
      })
      editor.on('ObjectResized', () => handleChangeAfterResize(editor))
      editor.on('dragOver', e => {
        preventDefault(e)
        // TinyMCE reacts to dragover like hover over table and opens contextual UI.
        // We must block internal handlers while dragging files.
        e.stopImmediatePropagation()

        onDragOver?.(e)
      })
      editor.on('drop', e => {
        preventDefault(e)
        stopPropagation(e)

        onDrop?.()

        const transferFiles = e.dataTransfer?.files

        if (transferFiles?.length > 0) {
          handleInlineFilesFromAnySource([...transferFiles])
        }
      })
    }
  }

  return {
    getInputProps,
    getRootProps,
    handleFocusOut,
    modal,
    setup,
  }
}
